import React from 'react';
import {
    CartIcon, CompassIcon, HeartIcon, HomeIcon, LuggageIcon, MapPinIcon, MessageSquareIcon,
    MonitorIcon, SparklesIcon, TrophyIcon, UsersIcon,
} from '../icons';
import {OutfitScenarioTile} from '../../support/outfitContext';

interface Props {
    tiles: OutfitScenarioTile[];
    loading?: boolean;
    error?: string | null;
    /** Идёт подбор по тайлу — повторное нажатие блокируется. */
    busyKey?: string | null;
    onPick: (tile: OutfitScenarioTile) => void;
}

/** Код иконки тайла → иконка набора. Незнакомый код не ломает сетку. */
const ICONS: Record<string, React.FC<{className?: string}>> = {
    monitor: MonitorIcon,
    users: UsersIcon,
    message: MessageSquareIcon,
    heart: HeartIcon,
    compass: CompassIcon,
    'map-pin': MapPinIcon,
    trophy: TrophyIcon,
    cart: CartIcon,
    luggage: LuggageIcon,
    home: HomeIcon,
};

/**
 * Сценарные тайлы первого экрана подбора (CTHS-105/161).
 *
 * Тайл — это ответ на вопрос «куда», а не кнопка «сделай хорошо»: под названием перечислено,
 * что именно он задаст. Поэтому у пользователя нет ощущения, что выбор происходит за него,
 * и расширенная панель нужна для уточнения, а не для старта.
 */
const OutfitScenarioTiles: React.FC<Props> = ({tiles, loading, error, busyKey, onPick}) => {
    if (loading) {
        return (
            <div className="rounded-2xl border border-gray-200 dark:border-ink-700 p-4 flex items-center gap-2"
                 data-testid="scenario-tiles-loading">
                <SparklesIcon className="w-4 h-4 text-brand-500 animate-pulse shrink-0"/>
                <span className="text-sm text-gray-500 dark:text-gray-400">Загружаем сценарии…</span>
            </div>
        );
    }
    if (error) {
        // Тайлы — ускорение, а не единственный путь: форма ниже продолжает работать.
        return (
            <p className="text-sm text-gray-500 dark:text-gray-400" data-testid="scenario-tiles-error">
                {error}
            </p>
        );
    }
    if (tiles.length === 0) {
        return null;
    }
    return (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2"
             data-testid="scenario-tiles">
            {tiles.map(tile => {
                const Icon = ICONS[tile.iconCode] ?? SparklesIcon;
                const busy = busyKey === tile.key;
                return (
                    <button key={tile.key} type="button" onClick={() => onPick(tile)}
                            disabled={Boolean(busyKey)}
                            data-testid={`scenario-tile-${tile.key}`}
                            title={(tile.hints ?? []).join('; ')}
                            className="min-h-[44px] rounded-xl border border-gray-200 dark:border-ink-700 p-3 text-left
                                       hover:border-brand-400 hover:bg-brand-50 dark:hover:bg-brand-900/20
                                       disabled:opacity-60 transition-colors">
                        <Icon className="w-5 h-5 text-brand-500 mb-1"/>
                        <span className="block text-sm font-medium text-gray-900 dark:text-gray-100">
                            {tile.nameRu}
                        </span>
                        <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            {busy ? 'Подбираем…' : (tile.hints ?? [])[0] ?? ''}
                        </span>
                    </button>
                );
            })}
        </div>
    );
};

export default OutfitScenarioTiles;
